import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Paper,
         Select, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow,
         TextField, Tooltip, Typography
} from '@mui/material';

import Add from '@mui/icons-material/Add';
import Apps from '@mui/icons-material/Apps';
import Contacts from '@mui/icons-material/Contacts';
import CreditCard from '@mui/icons-material/CreditCard';
import Edit from '@mui/icons-material/Edit';
import MonetizationOn from '@mui/icons-material/MonetizationOn';

import AccountLedgerSummaryFetch from './AccountLedgerSummaryFetch';
import CurrencyTextField from '../ui/CurrencyTextField';
import SaveButton from '../ui/SaveButton';

import AccountUpdateMutation from './AccountUpdateMutation';
import FundingSourceList from '../paymentsources/FundingSourceList';
import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import PhoneNumberFormat from '../ui/PhoneNumberFormat';
import Address from '../ui/Address'
import ServerError from '../ui/ServerError';
import AppLogo from '../ui/AppLogo';
import AppBindStyles from '../../AppBindStyles';

import AdminVendorSummary from './AdminVendorSummary';
import ConfirmationDialog from '../ui/ConfirmationDialog';

const abEnvironment = new AbEnvironment();
const VendorLookupQuery = graphql`
query VendorLookupQuery($accountId: UUID!, $keyName: String!) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    parentId
    accountType
    accountNum
    accountName
    description
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    logo
    stripeCid
    dwollaCid
    hubspotId
    achActive
    accountStatus
    feePlanId
    feePlanTByFeePlanId {
      feeCustAbsolute
      feeCustPercentage
      feeSelfAbsolute
      feeSelfPercentage
      feeMonthly
      feeAnnual
    }
    atId
    feeSelfAbsolute
    feeSelfPercentage
    feeCustAbsolute
    feeCustPercentage
    feeMonthly
    feeAnnual
    appTsByAccountId {
      nodes {
        appId
        appName
        appSku
        appUrl
        appLogo
        description
        appSkuPrice
      }
    }
    paymentSourceTsByAccountId {
      nodes {
        psId
        psOrder
        extSource
        extPsId
      }
    }
    userTsByAccountId(first: 20) {
      totalCount
      nodes {
        userId
        userName
        userRole
        lastName
        firstName
        email
        phone
        address1
        address2
        city
        state
        country
        postalCode
        marqetaUid
        emailVerified
      }
    }
  }
  getaccountachsources(accountid: $accountId) { totalCount } 
  allAccountKeyTs(condition: {accountId: $accountId, keyName: $keyName, active: 1}) {
    totalCount
    nodes {
      accountKeyId
      active
      keyType
      keyName
      keyValue
      lastUsed
      created
    }
  }
}
`;

const styleSheet = theme => ({
  tabroot: {
    flexGrow: 1,
    margin: 0,
    width: '100%',
    borderBottom: '2px solid #dedede'
  },
  flex: {
    flex: 1,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left',
    // color: theme.palette.text.secondary,
    maxWidth: 1200,
    marginTop: 16,
  },
  paper4: {
    //padding: theme.spacing(3),
    textAlign: 'left',
    // color: theme.palette.text.secondary,
    maxWidth: 1200,
    marginTop: 24,
  },
  avatar: {
    marginRight: 16,
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  select: {
    marginLeft: -8,
    marginTop: -8,
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusdiv: {
    display: 'flex',
    alignItems: 'center',
    direction: 'row',
    justify: 'center',
  },
  statusicon: {
    width: 52,
    height: 52,
    color: 'green',
  },
  erroricon: {
    width: 52,
    height: 52,
    color: 'red',
  },
  editiconbutton: {
    height: 34,
    marginRight: 20,
  },
  editicon: {
    width: 22,
    height: 22,
  },
  alsPaper: {
    padding: theme.spacing(1),
    // width: 200
  },
  /* Screen size specific styles */
  [theme.breakpoints.up('xs')]: {
    tabContainer: {
      padding: 0,
      overflow: 'hidden'
    },

    /* Tab navigation */
    tab: {
      fontSize: 'xx-small',
    },
    labelContainer: {
      paddingLeft: 4,
      paddingRight: 4,
    },
    label: {
      fontSize: 'xx-small',
    },
    labelWrapped: {
      fontSize: 'xx-small',
    },
  }
  ,
  [theme.breakpoints.up('sm')]: {
    tabContainer: {
      padding: 8,
      overflow: 'hidden'
    },

    /* Tab navigation */
    tab: {
      fontSize: 'x-small',
    },
    labelContainer: {
      paddingLeft: 8,
      paddingRight: 8,
    },
    label: {
      fontSize: 'x-small',
    },
    labelWrapped: {
      fontSize: 'x-small',
    },
  },
  [theme.breakpoints.up('md')]: {
    tabContainer: {
      padding: 20,
      overflow: 'hidden'
    },
  
    /* Tab navigation */
    tab: {
      fontSize: '0.75rem',
    },
    labelContainer: {
      paddingLeft: 12,
      paddingRight: 12,
    },
    label: {
      fontSize: '0.75rem',
    },
    labelWrapped: {
      fontSize: '0.75rem',
    },
  }
});

function TabContainer(props) {
  const classes = props.classes;
  return <div className={classes.tabContainer}>{props.children}</div>;
}

class Vendor extends Component {
  constructor(props) {
    super(props);

    const params = new URLSearchParams(this.props.location.search);
    let theTab = params.get('tab');

    this.state = {
      value: theTab == null ? 0: theTab,

      showSpinner: false,
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,

      accountId: null,
      accountName: null,
      accountNum: null,
      description: null,
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      country: null,
      postalCode: null,
      stripeCid: null,
      custApps: [],
      alsAssetBalance: null,
      alsLiabilityBalance: null,
      alsEquityBalance: null,
      alsMaxPayoutBalance: null,
      payout: 0.00,
      coverFees: true,
      accountStatus: null,

      verifyConfirm: false,
      verifyUserId: null
    }
  }

  handleChange = event => {
    this.setState({ name: event.target.value });
  };
  
  handleTabChange = (event, value) => {
    this.setState({ value });
  }

  goTo(route) {
    this.props.history.replace(`/${route}`);
  }

  
  goToPush(route) {
    this.props.history.push(`/${route}`);
  }

  goToCreatePayouts(route, achCount) {

    console.log(achCount);

    if (achCount === 0) {

      this.setState({ saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'The vendor needs ACH account setup.', saveSnackbarDuration: null });
      
    } else {
      this.props.history.push(`/${route}`);
    }
  }

  getValueForRender(state_member, prop_member, theDefault)
  {
    if (state_member != null)
      return state_member;
    
    if (prop_member != null)
      return prop_member;

    return theDefault;
  }

  disableAccountSave(accountProps) {
    if (accountProps === null)
      return true;

    let retval = this.disableAttributeCheck(this.state.accountName, accountProps.accountName) &&
                 this.disableAttributeCheck(this.state.accountNum,  accountProps.accountNum) &&
                 this.disableAttributeCheck(this.state.description, accountProps.description) &&
                 this.disableAttributeCheck(this.state.lastName,    accountProps.contactFirstName) &&
                 this.disableAttributeCheck(this.state.lastName,    accountProps.contactLastName) &&
                 this.disableAttributeCheck(this.state.email,       accountProps.contactEmail) &&
                 this.disableAttributeCheck(this.state.phone,       accountProps.contactPhone) &&
                 this.disableAttributeCheck(this.state.address1,    accountProps.address1) &&
                 this.disableAttributeCheck(this.state.address2,    accountProps.address2) &&
                 this.disableAttributeCheck(this.state.city,        accountProps.city) &&
                 this.disableAttributeCheck(this.state.state,       accountProps.state) &&
                 this.disableAttributeCheck(this.state.country,     accountProps.country) &&
                 this.disableAttributeCheck(this.state.postalCode,  accountProps.postalCode);

    return retval;
  }

  disableAdminSave(accountProps) {
    if (accountProps === null)
      return true;

    let retval = this.disableAttributeCheck(this.state.accountStatus, accountProps.accountStatus);

    return retval;
  }

  disableAttributeCheck(stateVar, propsVar)
  {
      if (stateVar !== null)
      {
          if (propsVar === null && stateVar === '')
              return true;
          
          if (propsVar !== stateVar)
              return false;
      }
      return true;
  }

  setCountry    = (country)    => { this.setState({country:    country}); }  
  setRegion     = (region)     => { this.setState({state:      region}); }  
  setCity       = (city)       => { this.setState({city:       city}); }  
  setPostalCode = (postalCode) => { this.setState({postalCode: postalCode}); }  
  setAddress1   = (address1)   => { this.setState({address1:   address1}); }  
  setAddress2   = (address2)   => { this.setState({address2:   address2}); }  
  
  masqueradeAs(u_id, a_id, role, userName) {
    this.props.auth.masqueradeAs(u_id, a_id, role, userName);
  }

  masqueradeAsCurrentUser(accountId) {
    this.props.auth.masqueradeAsCurrentUser(abEnvironment.getUserAttribute('u_id'), 
                                            accountId,
                                            'appbind_vendor',
                                            abEnvironment.getUserAttribute('userName'));
  }

  generateAPIKey(accountId) {
    const { callPartnerApi } = this.props.auth;

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "manageApiToken",
      params: {
        action: 'CreateKey',
        keyName: 'Extension',
        accountId: accountId
      }
    };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      window.location.reload();
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  rollAPIKey(accountId, keyId) {
    const { callPartnerApi } = this.props.auth;

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "manageApiToken",
      params: {
        keyId: keyId,
        action: 'RollKey',
        accountId: accountId
      }
    };

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      window.location.reload();
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  initiatePayout(accountId) {
    const { callPartnerApi } = this.props.auth;

    this.setState({saveStatus: 'saving'});

    let aPjwt = abEnvironment.getPjwt();
    let atId  = 'f5c70f86-135a-11e8-bb45-97ef56920c02';

    let payload = {
      operation: "initiatePayout",
      params: {
        pJwt: aPjwt,
        accountId: accountId,
        atId: atId,
        payoutAmount: this.state.payout,
        coverFees: this.state.coverFees
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      let abpResponse = response.data;
      if (abpResponse == null || abpResponse.status !== 0) {
        let theErrorText = "Unidentified Error";

        if (abpResponse != null && abpResponse.message != null) {
          theErrorText = abpResponse.message;
        }
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
        return;
      }

      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Payout sent', saveSnackbarDuration: 3000 });
    })
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
    });
  }

  disablePayoutButton() {
    let payoutVal = Number(this.state.payout);
    let maxPayoutVal = Number(this.state.alsMaxPayoutBalance);
    if (payoutVal <= 0 || payoutVal > maxPayoutVal) {
      return true;
    }
    return false;
  }

  toggleCoverFees = () => {
    if (this.state.coverFees === true) {
      this.setState({coverFees: false});
    } else {
      this.setState({coverFees: true});
    }     
  };

  showSpinner() {
    return this.state.showSpinner;
  }

  componentDidMount() {
    const { isAuthenticatedAppBind } = this.props.auth;

    if (isAuthenticatedAppBind()) {

      const params = new URLSearchParams(this.props.location.search);
      let acctId = params.get('accountId');
   
      AccountLedgerSummaryFetch.fetch(acctId).toPromise()
      .then(aef => {
          this.setState({alsAssetBalance: aef.getaccountledgersummary.assetBalance,
            alsLiabilityBalance: aef.getaccountledgersummary.liabilityBalance,
            alsEquityBalance: aef.getaccountledgersummary.equityBalance,
            alsMaxPayoutBalance: aef.getaccountledgersummary.maxPayoutBalance});

      })
      .catch(err => {
        //nothing to do....
      });

    }
  }

  getCurrencyDisplayValue(v) {
    let value = 0;
    if (v) {
      value = v;
    }
    let displayValue;
    try {
      displayValue = Number(value).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    } catch (err) {
      displayValue = Number(0).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
    }

    return displayValue;
  }

  render() {
    const { isAuthenticated, isAuthenticatedAppBind, isAdminUser, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;
    const { value } = this.state;

    // Experiences for different personas...
    if (!isAuthenticated() || needAssent()) {
      return (
        <Redirect to='/home' />
      );
    } else if (isAuthenticatedAppBind()) {
      const params = new URLSearchParams(this.props.location.search);
      let acctId = params.get('accountId');

      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={VendorLookupQuery}
          variables={{accountId: acctId, keyName: "Extension"}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              let fp = params.get('fp');
              let backTo = "Accounts";
              if (fp != null && fp !== '') {
                backTo = fp;
              }
    
              if (props.accountTByAccountId === null)
              {
                return (
                  <PageTitle title={"Vendor not found"} showBackButton={true} name={backTo} history={this.props.history}/>
                );
              }

              let uid = abEnvironment.getUserAttribute('u_id');  
              let stripeCid = props.accountTByAccountId.stripeCid;
              let dwollaCid = props.accountTByAccountId.dwollaCid;
              let psIds = [];
              for (var i=0; i<props.accountTByAccountId.paymentSourceTsByAccountId.nodes.length; i++)
              {
                let psId = props.accountTByAccountId.paymentSourceTsByAccountId.nodes[i].psId;
                psIds.push(psId);
              }

              let address = {
                country:    this.getValueForRender(this.state.country,    props.accountTByAccountId.country,    ''),
                state:      this.getValueForRender(this.state.state,      props.accountTByAccountId.state,      ''),
                city:       this.getValueForRender(this.state.city,       props.accountTByAccountId.city,       ''),
                postalCode: this.getValueForRender(this.state.postalCode, props.accountTByAccountId.postalCode, ''),
                address1:   this.getValueForRender(this.state.address1,   props.accountTByAccountId.address1,   ''),
                address2:   this.getValueForRender(this.state.address2,   props.accountTByAccountId.address2,   '')
              };

              let regionLabel = "Region";

              let newUserLink = "usercreate?accountId=" + props.accountTByAccountId.accountId;
              let viewLedgerLink = "accountledgers?accountId=" + props.accountTByAccountId.accountId +
                                   "&accountName=" + props.accountTByAccountId.accountName;

              // For passing to funding source component
              let uInfo = {
                accountId:   props.accountTByAccountId.accountId,
                accountName: props.accountTByAccountId.accountName,
                description: props.accountTByAccountId.description,
                firstName:   props.accountTByAccountId.contactFirstName,
                lastName:    props.accountTByAccountId.contactLastName,
                email:       props.accountTByAccountId.contactEmail,
                phone:       props.accountTByAccountId.contactPhone,
                address1:    props.accountTByAccountId.address1,
                address2:    props.accountTByAccountId.address2,
                city:        props.accountTByAccountId.city,
                state:       props.accountTByAccountId.state,
                country:     props.accountTByAccountId.country,
                postalCode:  props.accountTByAccountId.postalCode,
                dwollaCid:   dwollaCid,
                stripeCid:   stripeCid,
                achActive:   props.accountTByAccountId.achActive
              };

              let vendorName = this.getValueForRender(this.state.accountName, props.accountTByAccountId.accountName, '');

              let createPayoutLink = 'createpayouts?accountId=' + acctId;

              let apiKey = "Not assigned";
              let apiKeyId = null;
              if (props.allAccountKeyTs.totalCount !== 0) {
                apiKey = props.allAccountKeyTs.nodes[0].keyValue;
                apiKeyId = props.allAccountKeyTs.nodes[0].accountKeyId;
              }
              return (
                <Grid container spacing={3} direction="row" justifyContent="flex-start">
                  <Grid item xs={12}>
                    <PageTitle title="Vendor details" showBackButton={true} name={backTo} noMargin={true} history={this.props.history}/>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2} direction="row" justifyContent="space-between" style={{marginBottom: '10px'}}>
                        <Grid item>
                          <Grid container spacing={0} direction="row" justifyContent="flex-start" alignItems="center">
                            <Grid item className={classes.avatar}>
                              <AppLogo logoPath={props.accountTByAccountId.logo} appName={vendorName} imageHeight='48px'/>
                            </Grid>
                            <Grid item>
                              <Typography variant="h5" className={classes.flex}>
                                {vendorName}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid container spacing={0} direction="row" justifyContent="space-around">
                        <Grid item xs={12}>
                          <Tabs
                            value={value}
                            onChange={this.handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                            scrollButtons="auto"
                            classes={{root: classes.tabroot, scrollButtons: classes.tabScrollButton}}
                          >
                            <Tab label="Contact" icon={<Contacts />} classes={{root: classes.tab, wrapper: classes.labelWrapped}}/>
                            <Tab label="Funding" icon={<CreditCard />} classes={{root: classes.tab, wrapper: classes.labelWrapped}}/>
                            <Tab label="Apps" icon={<Apps />} classes={{root: classes.tab, wrapper: classes.labelWrapped}}/>
                            <Tab label="Invoices" icon={<MonetizationOn />} classes={{root: classes.tab, wrapper: classes.labelWrapped}}/>
                          </Tabs>
                        </Grid>
                        <Grid item xs={12} style={{marginTop: '10px'}}>
                          {value === 0 &&
                            <TabContainer {...this.props}>
                              <Grid container spacing={3}>
                                <Grid item xs={12}>
                                  <Grid container spacing={2} justifyContent="space-around">
                                    <Grid item xs={6} align="left">
                                      <Typography variant="h6" style={AppBindStyles.sectionTitleFirst}>
                                        Account
                                      </Typography>
                                    </Grid>
                                    {isAuthenticatedAppBind() &&
                                    <Grid item xs={6} align="right">
                                      <Button  
                                            color="primary" variant="outlined" 
                                            onClick={this.goToCreatePayouts.bind(this, createPayoutLink, props.getaccountachsources.totalCount)}>
                                          Create Reseller Payouts
                                      </Button>
                                    </Grid>
                                    }
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        id="customer-name" label="Vendor (business) name"
                                        value={ this.getValueForRender(this.state.accountName, 
                                                props.accountTByAccountId.accountName,
                                                '') }
                                        onChange={ (e) => this.setState({ accountName: e.target.value }) }
                                        inputProps={{ maxLength: 64 }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        id="account-num" label="Account number"
                                        value={ this.getValueForRender(this.state.accountNum, 
                                                props.accountTByAccountId.accountNum,
                                                '') }
                                        onChange={ (e) => this.setState({ accountNum: e.target.value }) }
                                        inputProps={{ maxLength: 64 }}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <TextField
                                        id="description" label="Description"
                                        value={ this.getValueForRender(this.state.description, 
                                                props.accountTByAccountId.description,
                                                '') }
                                        multiline
                                        onChange={ (e) => this.setState({ description: e.target.value }) }
                                        inputProps={{ maxLength: 1024 }}
                                      />
                                    </Grid>

                                    { this.state.alsAssetBalance != null &&
                                    <Grid item xs={12}>
                                      <Grid container spacing={2} justifyContent="flex-start">
                                        <Grid item xs={12}>
                                          <Typography variant="body2">
                                            Asset Balance: {this.state.alsAssetBalance}, Liability: {this.state.alsLiabilityBalance}, Equity: {this.state.alsEquityBalance}, Available to pay: {this.state.alsMaxPayoutBalance}
                                          </Typography>
                                        </Grid>
                                        <Grid item>
                                          <CurrencyTextField 
                                            id="payout" label="Payout"
                                            value={ this.state.payout }
                                            onChange={ (e) => this.setState({ payout: e.target.value }) }
                                            showErrMsg={true}
                                            allowNegative={false}
                                            minValue={0}
                                            minEnterValue={0}
                                            textAlign="right"
                                          />
                                        </Grid>
                                        <Grid item>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={(this.state.coverFees === true)}
                                              onChange={() => {this.toggleCoverFees()} }
                                              color="primary"
                                            />
                                          }
                                          label="AppBind cover fees"
                                        />
                                        </Grid>
                                        <Grid item>
                                          <SaveButton
                                            color="secondary"
                                            variant="outlined"
                                            label="Process payout"
                                            snackbarOpen={this.state.saveSnackbarOpen}
                                            autoHideDuration={this.state.saveSnackbarDuration}
                                            onSaveClick={() => { this.initiatePayout(acctId); }}
                                            onSnackbarClose={() => { this._onSnackbarClose(); }}
                                            status={this.state.saveStatus}
                                            message={this.state.saveMessage}
                                            disabled={this.disablePayoutButton()}/>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    }

                                    <Grid item xs={12}>
                                      <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                        Primary contact
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <TextField data-recording-ignore="mask"
                                        id="first-name" label="First name"
                                        value={ this.getValueForRender(this.state.firstName, 
                                                props.accountTByAccountId.contactFirstName,
                                                '') }
                                        onChange={ (e) => this.setState({ firstName: e.target.value }) }
                                        inputProps={{ maxLength: 32 }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <TextField data-recording-ignore="mask"
                                        id="last-name" label="Last name"
                                        value={ this.getValueForRender(this.state.lastName, 
                                                props.accountTByAccountId.contactLastName,
                                                '') }
                                        onChange={ (e) => this.setState({ lastName: e.target.value }) }
                                        inputProps={{ maxLength: 32 }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <TextField data-recording-ignore="mask"
                                        id="email" label="Email"
                                        value={ this.getValueForRender(this.state.email, 
                                                props.accountTByAccountId.contactEmail,
                                                '') }
                                        onChange={ (e) => this.setState({ email: e.target.value }) }
                                        inputProps={{ maxLength: 254 }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <TextField data-recording-ignore="mask"
                                        id="phone" label="Phone" 
                                        value={ this.getValueForRender(this.state.phone, 
                                                props.accountTByAccountId.contactPhone,
                                                '') }
                                        onChange={ (e) => this.setState({ phone: e.target.value }) }
                                        InputProps={{
                                          inputComponent: PhoneNumberFormat,
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                                        Mailing address
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Address
                                        address={address}
                                        regionLabel={regionLabel}
                                        setCountryCodeState={this.setCountry}
                                        setRegionCodeState={this.setRegion}
                                        setCityState={this.setCity}
                                        setPostalCodeState={this.setPostalCode}
                                        setAddress1State={this.setAddress1}
                                        setAddress2State={this.setAddress2}
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <Grid container spacing={2} direction="row" justifyContent="space-around">
                                    <Grid item xs={6} align="left">
                                      <Button disabled={this.disableAccountSave(props.accountTByAccountId)} color="primary" variant="contained" onClick={() => {
                                          this._updateVendor(acctId); 
                                      }}>
                                        Save
                                      </Button>
                                    </Grid>
                                    <Grid item xs={6}  align="right">
                                      <Button
                                        className={classes.mbutton}
                                        variant="outlined"
                                        onClick={this.masqueradeAsCurrentUser.bind(this, props.accountTByAccountId.accountId)}>
                                        Masquerade
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                  <Typography variant="h6" style={AppBindStyles.sectionTitleTable}>
                                    Users
                                  </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                  <Paper className={classes.paper4}>
                                    <Table>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Name</TableCell>
                                          <TableCell>Email</TableCell>
                                          <TableCell>Phone</TableCell>
                                          <TableCell>
                                            <Tooltip title="Add a vendor user">
                                              <Button
                                                className={classes.editiconbutton}
                                                variant="outlined"
                                                onClick={this.goTo.bind(this, newUserLink)}>
                                                <Add className={classes.editicon}/>&nbsp;Invite&nbsp;user
                                              </Button>
                                            </Tooltip>
                                          </TableCell>
                                          <TableCell/>
                                          <TableCell/>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {props.accountTByAccountId.userTsByAccountId.nodes.map(n => {
                                          let tlink = 'useredit?userId=' + n.userId 
                                                    + '&acctType=' + props.accountTByAccountId.accountType
                                                    + '&acctId=' + props.accountTByAccountId.accountId;
                                          return (
                                            <TableRow key={n.userId}>
                                              <TableCell data-recording-sensitive>{n.firstName} {n.lastName}</TableCell>
                                              <TableCell data-recording-sensitive>{n.email}</TableCell>
                                              <TableCell data-recording-sensitive>{n.phone}</TableCell>
                                              <TableCell>
                                                { uid !== n.userId &&
                                                <Button
                                                  className={classes.editiconbutton}
                                                  onClick={this.goTo.bind(this, tlink)}>
                                                  <Edit className={classes.editicon}/>
                                                </Button>
                                                }
                                              </TableCell>
                                              <TableCell>
                                                {n.emailVerified === 0 &&
                                                <Button
                                                  className={classes.vbutton}
                                                  variant="outlined"
                                                  onClick={this.confirmVerify.bind(this, n.userId)}>
                                                  Resend Verify
                                                </Button>
                                                }
      
                                              </TableCell>

                                              <TableCell>
                                                <Button
                                                  className={classes.mbutton}
                                                  variant="outlined"
                                                  onClick={this.masqueradeAs.bind(this, n.userId, props.accountTByAccountId.accountId, 'appbind_vendor', n.userName)}>
                                                  Masquerade
                                                </Button>
                                              </TableCell>

                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                    <ConfirmationDialog
                                        open={this.state.verifyConfirm}
                                        onClose={() => { this.setState({verifyConfirm:false, verifyUserId: null});}}
                                        onConfirm={() => { this.resendVerifyEmail();}}
                                        onCancel={() => { }}
                                        title="Resend verification email?"
                                        cancelBtn="No"
                                        okBtn="Yes, send"
                                      >
                                      {'User will receive an verify email. Do you wish to proceed?'}
                                    </ConfirmationDialog>

                                  </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant='h6'>Admin actions</Typography>
                                </Grid>
                                <Grid item style={{minWidth: 120}}>
                                  <FormControl variant="standard">
                                    <InputLabel shrink htmlFor="accountStatus">Account status</InputLabel>
                                    <Select
                                      value={this.getValueForRender(this.state.accountStatus, props.accountTByAccountId.accountStatus, 0)}
                                      inputProps={{
                                        name: 'accountStatus',
                                        id: 'accountStatus',
                                      }}
                                      onChange={(e) => { this.setState({ accountStatus: e.target.value }); } }
                                      disabled={props.accountTByAccountId.accountStatus === 2}
                                      className={classes.nselect}
                                    >
                                      <MenuItem value={0}>Active</MenuItem>
                                      <MenuItem value={1}>Suspended</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                { isAdminUser() && this.state.alsAssetBalance != null &&
                                <Grid item xs={12}>
                                  <Grid container spacing={2} justifyContent="flex-start">
                                    <Grid item xs={12}>
                                      <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-end">
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Typography align="center" variant="h6">Balance</Typography>
                                              </Grid>
                                              <Grid item>
                                                <Tooltip placement='bottom' title="Total equity - what we'll show as the balance for the account">
                                                  <Typography align="center" variant="body1">Equity balance</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsEquityBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title='Asset balance for the account - generally usable funds'>
                                                  <Typography align="center" variant="body1">Asset</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAssetBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title="Total liabilities - typically what is owed to AppBind, which should only occur when we provide a float.">
                                                  <Typography align="center" variant="body1">Liability</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsLiabilityBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title="This is the total virtual card charge amount that we've authorized but hasn't cleared yet.  We don't bill until the auth clears, as it could be reversed.">
                                                  <Typography align="center" variant="body1">Auths</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAuthBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title="These are inbound payments that haven't arrived yet (e.g. ACH top-up that hasn't cleared yet)">
                                                  <Typography align="center" variant="body1">Topup ttl</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsPayInAr)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Tooltip placement='bottom' title="This is the portion of inbound payments that haven't arrived yet that we've credited to the account for used in subscription purchases.">
                                                  <Typography align="center" variant="body1">Topup avl</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsPayInArAvailable)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Typography align="center" variant="h6">Available</Typography>
                                              </Grid>
                                              <Grid item>
                                                <Tooltip placement='bottom' title="Previous amount less any outstanding auths.  This is the amount that is shown to a reseller as available balance.">
                                                  <Typography align="center" variant="body1">Available balance</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsAssetAndArBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Typography align="center" variant="h6">Max Payout</Typography>
                                              </Grid>
                                              <Grid item>
                                                <Tooltip placement='bottom' title="The maximum amount we'll allow to be paid out.  This is asset less liabilities.">
                                                  <Typography align="center" variant="body1">Maximum payout</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsMaxPayoutBalance)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        <Grid item>
                                          <Paper className={classes.alsPaper}>
                                            <Grid container spacing={0} direction="column">
                                              <Grid item>
                                                <Typography align="center" variant="h6">Float</Typography>
                                              </Grid>
                                              <Grid item>
                                                <Tooltip placement='bottom' title="The amount we're currently floating to this account.">
                                                  <Typography align="center" variant="body1">Float balance</Typography>
                                                </Tooltip>
                                              </Grid>
                                              <Grid item><Typography align="center" variant="body2">{this.getCurrencyDisplayValue(this.state.alsFloatBalanceDisplay)}</Typography></Grid>
                                            </Grid>
                                          </Paper>
                                        </Grid>
                                        { isAdminUser() &&
                                        <Grid item>
                                          <Button 
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => {this.goToPush(viewLedgerLink);}}>
                                            View ledgers
                                          </Button>
                                        </Grid>}
                                        <Grid item>
                                          <Button 
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => {this.goToPush( 'featureflagmanage?accountId=' + props.accountTByAccountId.accountId);}}>
                                            Edit Feature Flags
                                          </Button>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    
                                    
                                    
                                  </Grid>
                                </Grid>}
                                <Grid item xs={12}>
                                  
                                  {apiKey === "Not assigned" &&
                                    <Grid container spacing={2} >
                                    <Grid item style={{marginTop: '8px'}}>
                                      <Typography><strong>Extension Key:</strong> not assigned</Typography>
                                    </Grid>
                                    <Grid item>
                                      <Button
                                        className={classes.mbutton}
                                        variant="outlined"
                                        onClick={this.generateAPIKey.bind(this, props.accountTByAccountId.accountId)}>
                                        Generate Extension API Key
                                      </Button>
                                      </Grid>
                                    </Grid>
                                  }
                                  {apiKey !== "Not assigned" &&
                                    <Grid container spacing={2}>
                                    <Grid item  style={{marginTop: '8px'}}>
                                        <Typography><strong>Extension Key:</strong> {apiKey}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                        className={classes.mbutton}
                                        variant="outlined"
                                        onClick={this.rollAPIKey.bind(this, props.accountTByAccountId.accountId, apiKeyId)}>
                                        Roll Extension API Key
                                      </Button>
                                    </Grid>
                                    </Grid>                                 
                                  }
                                  
                                </Grid>

                                <Grid item xs={12}>
                                  <SaveButton
                                    snackbarOpen={this.state.saveSnackbarOpen}
                                    autoHideDuration={this.state.saveSnackbarDuration}
                                    onSaveClick={() => { this._updateVendor(acctId); }}
                                    onSnackbarClose={() => { this._onSnackbarClose(); }}
                                    status={this.state.saveStatus}
                                    message={this.state.saveMessage}
                                    disabled={this.disableAdminSave(props.accountTByAccountId)}/>
                                </Grid>

                              </Grid>
                            </TabContainer>
                          }
                          {value === 1 &&
                            <TabContainer {...this.props}>
                              <FundingSourceList
                                cp="vendor"
                                accountId={props.accountTByAccountId.accountId}
                                uInfo={uInfo}
                                auth={this.props.auth}
                                accountStatus={props.accountTByAccountId.accountStatus}
                              />
                            </TabContainer>
                          }
                          {value === 2 &&
                            <TabContainer {...this.props}>
                              <Grid container spacing={2} justifyContent="space-around">
                                <Grid item xs={12}>
                                  <Grid container spacing={0} direction="column" justifyContent="flex-start">
                                    <Grid item>
                                      <Typography variant="h6" >Apps</Typography>
                                    </Grid>
                                    <Grid item>
                                      <Typography variant="body2">Apps provided by this vendor</Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <Table>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>App name</TableCell>
                                        <TableCell>Subscription plan</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Price</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {props.accountTByAccountId.appTsByAccountId.nodes.map(n => {
                                        let sprice = Number(n.appSkuPrice).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

                                        return (
                                          <TableRow key={n.appId}>
                                            <TableCell>{n.appName}</TableCell>
                                            <TableCell>{n.appSku}</TableCell>
                                            <TableCell>{n.description}</TableCell>
                                            <TableCell>{sprice}</TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Grid>
                                <Grid item xs={12} style={AppBindStyles.sectionTitle}>
                                  <Grid container spacing={0} direction="column" justifyContent="flex-start">
                                    <Grid item>
                                      <Typography variant="h6">Resellers</Typography>
                                    </Grid>
                                    <Grid item>
                                      <Typography variant="body2">Resellers that have sold apps provided by this vendor</Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                  <AdminVendorSummary vendorAccountId={acctId} auth={this.props.auth} history={this.props.history}/>
                                </Grid>
                              </Grid>

                            </TabContainer>
                          }
                          {value === 3 &&
                            <Typography {...this.props}>TODO</Typography>
                          }
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              );
            }
            return <LoadingSpinner />
          }}
        />
      );
    } else {
        return (
          <Redirect to='/home' />
        );
    }
    
  }

  _onSnackbarClose() {
    this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
  }

  confirmVerify = (userId) => {
    this.setState({verifyUserId: userId, verifyConfirm: true});
  }

  resendVerifyEmail  = () => {

    const { callPartnerApi, logError } = this.props.auth;

 
    let payload = {
      operation: "resendVerifyEmail",
      params: {
        appBindUserId: this.state.verifyUserId
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      // console.log(JSON.stringify(response));
      let abpResponse = response.data;
      if (abpResponse == null || abpResponse.user == null) {
        let theErrorText = "Unidentified Error";

        if (abpResponse != null && abpResponse.message != null) {
          theErrorText = abpResponse.message;
        }
        logError('Error in resending verificatioh email: ' + theErrorText);
        this.setState({ verifyUserId: null, verifyConfirm: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Problem sending email', saveSnackbarDuration: null });
        return;
      }

      this.setState({ verifyUserId: null, verifyConfirm: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Email sent', saveSnackbarDuration: 3000 });
    })
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ verifyUserId: null, verifyConfirm: false, saveSnackbarOpen: true, saveStatus: 'Problem sending email', saveMessage: theErrorText, saveSnackbarDuration: null });
    });

  }

  _updateVendor(acctId) {
    const { logError } = this.props.auth;

    const { accountName, accountNum, description, accountStatus,
      lastName, firstName, email, phone,
      address1, address2, city, state, country, postalCode } = this.state;

    AccountUpdateMutation.commit(acctId, accountName, accountNum, description, accountStatus,
                                 lastName, firstName, email, phone, 
                                 address1, address2, city, state, country, postalCode,
                                 null, null, null, null, null, 
                                 null, null, null)
                          .then(response => {
                            // console.log('Account updated')
                          })
                          .catch(error => {
                            logError('Exception in Vendor.js: ' + error);
                          });
  }
}

Vendor.propTypes = {
  classes: PropTypes.object.isRequired,
};
    
export default withStyles(styleSheet)(Vendor);
